import React, { useEffect, useCallback, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import {
  Search as SearchIcon,
  Copy,
  Check,
  X,
  Pen,
  SquarePen,
  ArrowUpRight,
  RefreshCw,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';

import { FileType } from './types';

interface ResultsProps {
  completion: string;
  isLoading: boolean;
  stage: string;
  loadingStage: string | null;
  files: {
    data: FileType[];
  };
  hideReferences: boolean;
  resultSearchTerm?: string;
  setResultSearchTerm?: React.Dispatch<React.SetStateAction<string>>;
  onCopyContent?: (content: string) => void;
  copySuccess?: boolean;
  onClearCompletion?: () => void;
  onEditQuery?: (messageIndex: number, newContent: string) => void;
  onRegenerateResponse?: (messageIndex: number) => void;
  conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  hideToolbar?: boolean;
}

export const formatTimestamp = (seconds: string): string => {
  const totalSeconds = parseInt(seconds, 10);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Highlight search terms in text
const highlightSearchTerm = (
  text: string | React.ReactNode,
  searchTerm?: string,
): React.ReactNode => {
  if (!searchTerm || typeof text !== 'string') return text;

  try {
    // Handle multiple words in search term
    const searchTerms = searchTerm
      .trim()
      .split(/\s+/)
      .filter((term) => term.length > 0);

    // If no valid search terms, return the original text
    if (searchTerms.length === 0) return text;

    // Create a single regex pattern with all search terms as alternatives
    const escapedTerms = searchTerms.map((term) =>
      term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    );

    const pattern = escapedTerms.join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');

    // Split the text by the search terms, keeping the delimiters
    const parts = text.split(regex);

    // If no matches found (split returns single element), return original text
    if (parts.length === 1) return text;

    // Build the result, highlighting every odd-indexed part (the matches)
    return parts.map((part, i) => {
      // Even indices are non-matches, odd indices are matches
      if (i % 2 === 0) {
        return part;
      }
      // This is a match, highlight it
      return (
        <span
          key={i}
          style={{
            backgroundColor: '#fff176',
          }}
        >
          {part}
        </span>
      );
    });
  } catch (error) {
    console.error('Search highlighting error:', error);
    return text;
  }
};

type MarkdownLinkProps = {
  href?: string;
  children?: React.ReactNode;
};

// Custom components for ReactMarkdown
const createMarkdownComponents = (
  files: {
    data: FileType[];
  },
  hideReferences: boolean,
  resultSearchTerm?: string,
) => ({
  h1: ({ children, ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
    <h1
      style={{
        fontSize: '1.125rem',
        fontWeight: 'bold',
        marginBottom: '0.5rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h1>
  ),
  h2: ({ children, ...props }: React.ComponentPropsWithoutRef<'h2'>) => (
    <h2
      style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h2>
  ),
  h3: ({ children, ...props }: React.ComponentPropsWithoutRef<'h3'>) => (
    <h3
      style={{
        fontSize: '0.9375rem',
        fontWeight: 'bold',
        marginBottom: '0.375rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h3>
  ),
  h4: ({ children, ...props }: React.ComponentPropsWithoutRef<'h4'>) => (
    <h4
      style={{
        fontSize: '0.875rem',
        fontWeight: 'bold',
        marginBottom: '0.375rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h4>
  ),
  h5: ({ children, ...props }: React.ComponentPropsWithoutRef<'h5'>) => (
    <h5
      style={{
        fontSize: '0.8125rem',
        fontWeight: 'bold',
        marginBottom: '0.25rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h5>
  ),
  h6: ({ children, ...props }: React.ComponentPropsWithoutRef<'h6'>) => (
    <h6
      style={{
        fontSize: '0.75rem',
        fontWeight: 'bold',
        marginBottom: '0.25rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </h6>
  ),
  p: ({ children, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
    <p
      style={{
        fontSize: '0.875rem',
        lineHeight: '1.5',
        marginBottom: '0',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </p>
  ),
  strong: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<'strong'>) => (
    <strong
      style={{
        fontWeight: 600,
        display: 'inline',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </strong>
  ),
  ul: ({ children, ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
    <ul
      style={{
        paddingLeft: '1.5em',
        marginTop: '0.5em',
        marginBottom: '0.5em',
        listStyleType: 'disc',
      }}
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
    <ol
      style={{
        paddingLeft: '1.5em',
        marginTop: '0.5em',
        marginBottom: '0.5em',
      }}
      {...props}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) => (
    <li
      style={{
        fontSize: '0.875rem',
        marginBottom: '0.375rem',
        marginTop: '0.375rem',
        lineHeight: '1.5',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </li>
  ),
  table: ({ children, ...props }: React.ComponentPropsWithoutRef<'table'>) => (
    <table style={{ borderCollapse: 'collapse', width: '100%' }} {...props}>
      {children}
    </table>
  ),
  th: ({ children, ...props }: React.ComponentPropsWithoutRef<'th'>) => (
    <th
      style={{
        fontSize: '0.875rem',
        border: '1px solid hsl(var(--border))',
        padding: '6px',
        backgroundColor: 'hsl(var(--muted))',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </th>
  ),
  td: ({ children, ...props }: React.ComponentPropsWithoutRef<'td'>) => (
    <td
      style={{
        fontSize: '0.875rem',
        border: '1px solid hsl(var(--border))',
        padding: '6px',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </td>
  ),
  code: ({ children, ...props }: React.ComponentPropsWithoutRef<'code'>) => (
    <code
      style={{
        backgroundColor: 'hsl(var(--muted))',
        padding: '2px 4px',
        borderRadius: '4px',
        fontSize: '0.8125rem',
      }}
      {...props}
    >
      {children}
    </code>
  ),
  blockquote: ({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<'blockquote'>) => (
    <blockquote
      style={{
        fontSize: '0.875rem',
        borderLeft: '3px solid hsl(var(--border))',
        marginLeft: 0,
        paddingLeft: '12px',
        fontStyle: 'italic',
        marginBottom: '0.75rem',
      }}
      {...props}
    >
      {highlightSearchTerm(children as string, resultSearchTerm)}
    </blockquote>
  ),
  hr: ({ ...props }: React.ComponentPropsWithoutRef<'hr'>) => (
    <hr
      style={{
        border: 'none',
        borderTop: '1px solid hsl(var(--border))',
        margin: '1em 0',
      }}
      {...props}
    />
  ),
  a: ({ href, children }: MarkdownLinkProps) => {
    if (href?.includes('@')) {
      if (hideReferences) return null;
      const [fileId, timestamp] = href.split('@');
      const file = files.data.find((f) => f._id === fileId);

      return (
        <button
          type="button"
          onClick={() => {
            const baseUrl = window.location.pathname.includes('/app')
              ? '/app/#'
              : '/#';
            window.open(
              `${baseUrl}/library/${file?.teamId || 'unknown'}/recents/${fileId}?t=${timestamp}`,
              '_blank',
            );
          }}
          className={cn(
            'inline cursor-pointer align-middle transition-all duration-150 hover:scale-110 border-0 bg-transparent p-0',
          )}
          title={
            file
              ? `Go to: ${file.name} at ${formatTimestamp(timestamp)}`
              : 'Go to source...'
          }
        >
          <ArrowUpRight
            size={18}
            className={cn(
              'relative -top-0.5 inline-block px-0.5 align-middle transition-colors duration-150 text-gray-400 hover:text-primary',
            )}
            strokeWidth={1.5}
          />
        </button>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline"
      >
        {highlightSearchTerm(children as string, resultSearchTerm)}
      </a>
    );
  },
});

const Results: React.FC<ResultsProps> = ({
  completion,
  isLoading,
  stage,
  files,
  hideReferences,
  resultSearchTerm: externalResultSearchTerm,
  setResultSearchTerm: externalSetResultSearchTerm,
  onClearCompletion,
  onEditQuery,
  onRegenerateResponse,
  conversationHistory = [],
  hideToolbar = false,
}) => {
  const [internalResultSearchTerm, setInternalResultSearchTerm] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(
    null,
  );
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(
    null,
  );
  const [editedContent, setEditedContent] = useState('');
  const editTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const lastScrollTopRef = useRef<number>(0);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAutoScrollingRef = useRef(false);

  // Detect if user is near bottom of scroll container
  const isNearBottom = useCallback((container: HTMLElement) => {
    const threshold = 150; // pixels from bottom
    const position = container.scrollTop + container.clientHeight;
    const height = container.scrollHeight;
    return height - position < threshold;
  }, []);

  // Track user scrolling (ignore auto-scroll events)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      // Ignore scroll events triggered by auto-scroll
      if (isAutoScrollingRef.current) {
        return;
      }

      const currentScrollTop = container.scrollTop;

      // User scrolled up significantly (more than 5px to avoid jitter)
      if (currentScrollTop < lastScrollTopRef.current - 5) {
        setUserHasScrolled(true);
      }

      // User scrolled near bottom, resume auto-scroll
      if (isNearBottom(container)) {
        setUserHasScrolled(false);
      }

      lastScrollTopRef.current = currentScrollTop;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isNearBottom]);

  // Reset scroll flag when conversation history changes (new message)
  useEffect(() => {
    setUserHasScrolled(false);
  }, [conversationHistory.length]);

  // Scroll to bottom when response generation completes
  useEffect(() => {
    if (!isLoading && stage === 'final' && messagesEndRef.current) {
      // Small delay to ensure DOM has updated
      const scrollTimeout = setTimeout(() => {
        if (messagesEndRef.current) {
          isAutoScrollingRef.current = true;
          messagesEndRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });

          // Reset scroll flag to allow auto-scroll for next message
          setUserHasScrolled(false);

          // Reset flag after scroll animation completes
          setTimeout(() => {
            isAutoScrollingRef.current = false;
          }, 400);
        }
      }, 100);

      return () => clearTimeout(scrollTimeout);
    }
    return undefined;
  }, [isLoading, stage]);

  // Throttled auto-scroll during streaming: batch scroll updates to prevent flicker
  useEffect(() => {
    if (
      !messagesEndRef.current ||
      stage !== 'final' ||
      userHasScrolled ||
      !isLoading
    ) {
      return undefined;
    }

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Throttle scroll updates to every 150ms instead of every word (15ms)
    scrollTimeoutRef.current = setTimeout(() => {
      if (messagesEndRef.current && !userHasScrolled) {
        isAutoScrollingRef.current = true;
        messagesEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });

        // Reset flag after scroll animation completes
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, 400);
      }
    }, 150);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [
    completion,
    conversationHistory.length,
    stage,
    userHasScrolled,
    isLoading,
  ]);

  // Use external search term if provided, otherwise use internal
  const resultSearchTerm = externalResultSearchTerm ?? internalResultSearchTerm;
  const setResultSearchTerm =
    externalSetResultSearchTerm ?? setInternalResultSearchTerm;

  // Fix malformed references in the completion text
  const fixMalformedReferences = useCallback((text: string): string => {
    if (!text) return '';

    // Fix UUID-based references with missing closing parenthesis: [→](uuid@timestamp] -> [→](uuid@timestamp)
    let fixedText = text.replace(
      /\[→\]\(([a-f0-9-]+@\d+(?:\.\d+)?)\]/g,
      '[→]($1)',
    );

    // Fix regular timestamp references with bracket closing: [→](47] -> [→](47)
    fixedText = fixedText.replace(/\[→\]\((\d+(?:\.\d+)?)\]/g, '[→]($1)');

    return fixedText;
  }, []);

  // Count matches in text
  const countMatches = useCallback((text: string, term: string): number => {
    if (!term || !text) return 0;
    try {
      const searchTerms = term
        .trim()
        .split(/\s+/)
        .filter((t) => t.length > 0);
      if (searchTerms.length === 0) return 0;

      const escapedTerms = searchTerms.map((t) =>
        t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      );
      const pattern = `(${escapedTerms.join('|')})`;
      const regex = new RegExp(pattern, 'gi');

      // Count matches in markdown text
      return (text.match(regex) || []).length;
    } catch (error) {
      console.error('Error counting matches:', error);
      return 0;
    }
  }, []);

  // Update match count when search term changes
  useEffect(() => {
    if (resultSearchTerm && completion) {
      const count = countMatches(completion, resultSearchTerm);
      setMatchCount(count);
    } else {
      setMatchCount(0);
    }
  }, [resultSearchTerm, completion, countMatches]);

  // Add ESC key listener to clear search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && resultSearchTerm) {
        setResultSearchTerm('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [resultSearchTerm]);

  // Auto-scroll to bottom when conversation history changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationHistory]);

  // Create markdown components with the current state
  const markdownComponents = useCallback(
    () => createMarkdownComponents(files, hideReferences, resultSearchTerm),
    [files, hideReferences, resultSearchTerm],
  );

  // Don't return null if we're in the loading state
  if (
    !completion &&
    !isLoading &&
    stage !== 'processing' &&
    conversationHistory.length === 0
  ) {
    return null;
  }

  // Show toolbar whenever there's at least one message in the conversation
  // This includes both user messages and assistant responses
  const shouldShowToolbar = Boolean(
    conversationHistory.length > 0 && !hideToolbar,
  );

  return (
    <div className="flex flex-col h-full">
      {/* Action Bar - Minimal toolbar pinned at top with collapse animation */}
      <Collapsible open={shouldShowToolbar}>
        <CollapsibleContent className="overflow-hidden transition-all duration-300 data-[state=closed]:animate-[collapsible-up_300ms_cubic-bezier(0.4,0.0,0.2,1)] data-[state=open]:animate-[collapsible-down_300ms_cubic-bezier(0.4,0.0,0.2,1)]">
          <div className="bg-background/95 backdrop-blur-sm border-b border-border/40">
            <div className="max-w-3xl mx-auto flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <div
                  className={`relative transition-all duration-300 ${
                    showSearchInput ? 'w-48 h-8' : 'w-8 h-8'
                  }`}
                >
                  {showSearchInput ? (
                    <Input
                      type="text"
                      placeholder="Search in results..."
                      value={resultSearchTerm || ''}
                      onChange={(e) => setResultSearchTerm?.(e.target.value)}
                      onBlur={() => {
                        if (!resultSearchTerm) {
                          setShowSearchInput(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setResultSearchTerm?.('');
                          setShowSearchInput(false);
                          e.preventDefault();
                        }
                      }}
                      className="h-8 text-sm rounded-full pl-8 pr-8 bg-background"
                      autoFocus
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowSearchInput(true)}
                      className="w-8 h-8 rounded-full hover:bg-accent/50 flex items-center justify-center transition-colors"
                    >
                      <SearchIcon className="h-4 w-4 text-muted-foreground" />
                    </button>
                  )}

                  {showSearchInput && (
                    <>
                      <SearchIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      {resultSearchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setResultSearchTerm('');
                            setShowSearchInput(false);
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-blue-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onClearCompletion?.()}
                  className="w-8 h-8 rounded-full hover:bg-accent/50 flex items-center justify-center transition-colors"
                  title="New chat"
                >
                  <SquarePen className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Content area - scrollable */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 pt-4 pb-[70px]"
      >
        <div className="max-w-3xl mx-auto">
          {completion && !isLoading && resultSearchTerm && (
            <div className="flex items-center justify-between mb-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
              <div className="flex items-center">
                <SearchIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                <p className="text-sm text-muted-foreground">
                  {matchCount > 0
                    ? `Found ${matchCount} match${matchCount !== 1 ? 'es' : ''}`
                    : 'No matches'}{' '}
                  for{' '}
                  <span className="font-bold text-blue-600 dark:text-blue-400">
                    &ldquo;{resultSearchTerm}&rdquo;
                  </span>
                </p>
              </div>
              <div className="flex items-center ml-auto bg-background px-2 py-0.5 rounded-lg text-xs text-muted-foreground">
                ESC to clear
              </div>
            </div>
          )}

          {/* Conversation Messages */}
          <div className="space-y-0 smooth-scroll">
            {conversationHistory.map((message, index) => {
              const isEditing = editingMessageIndex === index;

              return (
                <div
                  key={index}
                  className={cn(
                    'flex items-start gap-1 py-1 group w-full animate-fade-in-up',
                    message.role === 'user' ? 'flex-row-reverse' : 'flex-row',
                  )}
                >
                  <div
                    className={cn(
                      'flex flex-col',
                      message.role === 'user' ? 'items-end' : 'items-start',
                    )}
                  >
                    <div
                      className={cn(
                        'relative px-4 py-2 rounded-xl w-fit mb-0.5 text-sm leading-6 tracking-normal',
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground rounded-tr-md ml-auto'
                          : 'bg-muted rounded-tl-md',
                        message.role === 'user' &&
                          isEditing &&
                          'min-w-[300px] pb-10',
                      )}
                    >
                      <div className="break-words">
                        {(() => {
                          if (message.role === 'user') {
                            if (isEditing) {
                              return (
                                <Textarea
                                  ref={editTextareaRef}
                                  value={editedContent}
                                  onChange={(e) => {
                                    setEditedContent(e.target.value);
                                    // Auto-resize
                                    e.target.style.height = 'auto';
                                    e.target.style.height = `${e.target.scrollHeight}px`;
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      if (editedContent.trim()) {
                                        onEditQuery?.(index, editedContent);
                                        setEditingMessageIndex(null);
                                        setEditedContent('');
                                      }
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingMessageIndex(null);
                                      setEditedContent('');
                                    }
                                  }}
                                  className="w-full bg-transparent text-primary-foreground border-0 resize-none p-0 m-0 min-h-0 h-auto focus-visible:ring-0 focus-visible:ring-offset-0 outline-none shadow-none overflow-hidden"
                                  style={{
                                    lineHeight: '1.5',
                                    fontSize: '0.875rem',
                                  }}
                                  autoFocus
                                />
                              );
                            }
                            return (
                              <p className="break-words whitespace-pre-wrap m-0">
                                {highlightSearchTerm(
                                  message.content,
                                  resultSearchTerm,
                                )}
                              </p>
                            );
                          }
                          return (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={markdownComponents()}
                            >
                              {fixMalformedReferences(message.content)}
                            </ReactMarkdown>
                          );
                        })()}
                      </div>

                      {/* Cancel and Send buttons on their own line when editing */}
                      {message.role === 'user' && isEditing && (
                        <div className="absolute bottom-2 right-2 flex items-center justify-end gap-1.5 mt-2">
                          {/* Cancel button */}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingMessageIndex(null);
                              setEditedContent('');
                            }}
                            className="px-2.5 py-1 rounded-full transition-all bg-primary-foreground/60 text-primary hover:bg-primary-foreground/80 text-xs font-medium"
                            title="Cancel editing"
                          >
                            Cancel
                          </button>
                          {/* Send button */}
                          <button
                            type="button"
                            onClick={() => {
                              if (editedContent.trim()) {
                                onEditQuery?.(index, editedContent);
                                setEditingMessageIndex(null);
                                setEditedContent('');
                              }
                            }}
                            disabled={!editedContent.trim()}
                            className={cn(
                              'px-2.5 py-1 rounded-full transition-all text-xs font-medium',
                              editedContent.trim()
                                ? 'bg-primary-foreground text-primary hover:bg-primary-foreground/90'
                                : 'bg-primary-foreground/30 text-primary/50 cursor-not-allowed',
                            )}
                            title="Send edited message"
                          >
                            Send
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Edit button below bubble when NOT editing */}
                    {message.role === 'user' && !isEditing && (
                      <div className="flex items-center gap-1 mr-2 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingMessageIndex(index);
                            setEditedContent(message.content);
                            setTimeout(() => {
                              if (editTextareaRef.current) {
                                editTextareaRef.current.style.height = 'auto';
                                editTextareaRef.current.style.height = `${editTextareaRef.current.scrollHeight}px`;
                              }
                            }, 0);
                          }}
                          className="p-1 rounded hover:bg-accent transition-colors"
                          title="Edit message"
                        >
                          <Pen className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </div>
                    )}

                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-1 ml-2 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(message.content);
                            setCopiedMessageIndex(index);
                            setTimeout(() => setCopiedMessageIndex(null), 2000);
                          }}
                          className="p-1 rounded hover:bg-muted transition-colors"
                          title="Copy message"
                        >
                          {copiedMessageIndex === index ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            onRegenerateResponse?.(index);
                          }}
                          className="p-1 rounded hover:bg-muted transition-colors"
                          title="Regenerate response"
                        >
                          <RefreshCw className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Show loading indicator when still loading but no content yet */}
            <Collapsible open={isLoading && stage === 'processing'}>
              <CollapsibleContent className="overflow-hidden transition-all duration-500 data-[state=closed]:animate-[collapsible-up_500ms_cubic-bezier(0.4,0.0,0.2,1)] data-[state=open]:animate-[collapsible-down_500ms_cubic-bezier(0.4,0.0,0.2,1)]">
                <div className="flex items-start gap-1 py-2 w-full">
                  <div className="flex flex-col items-start">
                    <div className="relative px-4 py-2 rounded-xl w-fit mb-0.5 text-sm leading-6 tracking-normal bg-muted rounded-tl-md">
                      <div className="flex items-center gap-1 py-2">
                        {[0, 1, 2].map((index) => (
                          <span
                            key={index}
                            className={cn(
                              'w-2 h-2 rounded-full bg-muted-foreground opacity-70',
                              'animate-pulse',
                            )}
                            style={{
                              animationDelay: `${index * 0.2}s`,
                              animationDuration: '1.5s',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Streaming message - shows completion as it streams */}
            {/* Only show streaming message if it's not already in conversation history */}
            {completion &&
              stage === 'final' &&
              isLoading &&
              conversationHistory[conversationHistory.length - 1]?.role !==
                'assistant' && (
                <div className="flex items-start gap-1 py-1 w-full">
                  <div className="flex flex-col items-start">
                    <div className="px-4 py-2 rounded-xl bg-muted rounded-tl-md">
                      <div className="break-words text-sm leading-6 tracking-normal">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents()}
                        >
                          {fixMalformedReferences(completion)}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Invisible element at the end for auto-scrolling */}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Results;
